<template>
  <IconTile size="small" shape="rounded" :colored-icon="true" :icon="icon.iconName" :color="icon.color" />
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { IconTile, IconName, IconTileColor } from '@tallo/design-system';

import { ActivityFeedEventName } from '../enums/activity-feed-event-name.enum';

interface IconMapItem {
  iconName: IconName;
  color: IconTileColor;
}

const props = defineProps<{ eventName: ActivityFeedEventName }>();
const iconMap = new Map<ActivityFeedEventName, IconMapItem>([
  [ActivityFeedEventName.APPLICATION_SENT, { iconName: 'file-sent-02', color: 'blue' }],
  [ActivityFeedEventName.APPLICATION_COMPLETED, { iconName: 'file-check-02', color: 'green' }],
  [ActivityFeedEventName.APPLICATION_REQUESTED, { iconName: 'file-question-02', color: 'orange' }],
  [ActivityFeedEventName.APPLICATION_STARTED, { iconName: 'file-04', color: 'blue' }],
  [ActivityFeedEventName.CO_APPLICANT_INVITED, { iconName: 'users-plus', color: 'violet' }],
  [ActivityFeedEventName.CO_SIGNER_INVITED, { iconName: 'user-plus-01', color: 'violet' }],
  [ActivityFeedEventName.APPLICANT_REMOVED, { iconName: 'x-close-circle', color: 'red' }],
  [ActivityFeedEventName.SCREENING_FEE_PAID, { iconName: 'check-done-01', color: 'green' }],
  [ActivityFeedEventName.BACKGROUND_CHECK_COMPLETED, { iconName: 'check-circle', color: 'blue' }],
  [ActivityFeedEventName.APPLICATION_READY_TO_SUBMIT, { iconName: 'clipboard-check', color: 'orange' }],
  [ActivityFeedEventName.APPLICATION_APPROVED, { iconName: 'thumbs-up', color: 'green' }],
  [ActivityFeedEventName.APPLICATION_DECLINED, { iconName: 'thumbs-down', color: 'red' }],
  [ActivityFeedEventName.APPLICATION_UPDATE_REQUESTED, { iconName: 'file-question-02', color: 'violet' }],
  [ActivityFeedEventName.APPLICATION_RESUBMITTED, { iconName: 'file-check-02', color: 'green' }],
  [ActivityFeedEventName.SHOWING_ATTENDANCE_CONFIRMED, { iconName: 'calendar-check-02', color: 'green' }],
  [ActivityFeedEventName.SHOWING_REQUESTED, { iconName: 'calendar-question', color: 'orange' }],
  [ActivityFeedEventName.SHOWING_CONFIRMED, { iconName: 'calendar-check-02', color: 'green' }],
  [ActivityFeedEventName.SHOWING_COMPLETED, { iconName: 'calendar-check-01', color: 'blue-dust' }],
  [ActivityFeedEventName.SHOWING_ATTENDANCE_DECLINED, { iconName: 'calendar-x-02', color: 'red' }],
  [ActivityFeedEventName.NEW_LEAD, { iconName: 'user-plus-01', color: 'violet' }],
  [ActivityFeedEventName.PROPERTY_ADDED, { iconName: 'home-plus', color: 'neutral' }],
  [ActivityFeedEventName.PROPERTY_LISTED, { iconName: 'clipboard-check', color: 'blue' }],
  [ActivityFeedEventName.PROPERTY_RENTED_OUT, { iconName: 'home-check', color: 'green' }],
  [ActivityFeedEventName.LEAD_QUALIFIED, { iconName: 'user-check-01', color: 'green' }],
]);

// The backend can ship an event before a client picks up a build that maps it, so an unknown event
// gets a neutral tile rather than throwing during render.
const fallbackIcon: IconMapItem = { iconName: 'bell-01', color: 'neutral' };
const icon = computed(() => iconMap.get(props.eventName) ?? fallbackIcon);
</script>

<style scoped lang="scss"></style>
